// web/src/notes/NotesUIController.tsx
//
// Lifted notes-UI controller. Originally `openedTarget` + `notesOpen` lived
// inside `TradingView`; this context moves the controls to AppShell scope
// so the AskTapelens chat sheet's `useCopilotAction("open_notes")` handler
// can drive the same slide-over without prop drilling through the trading
// component tree.
//
// Shape:
//   * `openedTarget` — null when freeform (RecentNotesPanel), non-null
//     when scoped to a specific cluster/analysis/material (NotesSlideOver).
//   * `notesOpen` — whether the panel/slide-over is visible.
//   * `openNotes(target?)` — set the target and open. Pass null/undefined
//     for the freeform panel.
//   * `setNotesOpen` — direct setter; primarily for the slide-over's
//     onOpenChange callback (Sheet's controlled-close pattern).
//   * `closeNotes` — convenience that resets target + closes.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { NoteTargetKind } from "../api/notes";

export type NoteTarget = { kind: NoteTargetKind; id: string; title?: string };

type Ctx = {
  openedTarget: NoteTarget | null;
  notesOpen: boolean;
  openNotes: (target?: NoteTarget | null) => void;
  setNotesOpen: (open: boolean) => void;
  closeNotes: () => void;
};

const NotesUIControllerCtx = createContext<Ctx | null>(null);

export function NotesUIControllerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [openedTarget, setOpenedTarget] = useState<NoteTarget | null>(null);
  const [notesOpen, setNotesOpenState] = useState(false);

  const openNotes = useCallback((target?: NoteTarget | null) => {
    setOpenedTarget(target ?? null);
    setNotesOpenState(true);
  }, []);

  const setNotesOpen = useCallback((open: boolean) => {
    setNotesOpenState(open);
    if (!open) setOpenedTarget(null);
  }, []);

  const closeNotes = useCallback(() => {
    setOpenedTarget(null);
    setNotesOpenState(false);
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      openedTarget,
      notesOpen,
      openNotes,
      setNotesOpen,
      closeNotes,
    }),
    [openedTarget, notesOpen, openNotes, setNotesOpen, closeNotes],
  );

  return (
    <NotesUIControllerCtx.Provider value={value}>
      {children}
    </NotesUIControllerCtx.Provider>
  );
}

export function useNotesUI(): Ctx {
  const v = useContext(NotesUIControllerCtx);
  if (!v)
    throw new Error("useNotesUI used outside NotesUIControllerProvider");
  return v;
}
